import { useMemo } from 'react'

import { useListUsersApiV1UsersGet } from '#/shared/api/generated/auth/theCargoAuth'
import type { UserListItem } from '#/shared/api/generated/auth/models'
import { useListProviderOptionsApiV1ProvidersShortGet } from '#/shared/api/generated/shipment/theCargoShipment'
import type { MultiSelectOption } from '#/shared/ui/multi-select'

const USERS_PAGE_SIZE = 100

function userOption(user: UserListItem): MultiSelectOption {
  const name = [user.first_name, user.last_name]
    .filter(Boolean)
    .join(' ')
    .trim()
  return { value: user.id, label: name.length > 0 ? name : user.email }
}

/**
 * The org's users, for "Pick users" (`applies_to_user_ids`). Fetched only
 * once that chip is chosen — the dialog opens on "All users".
 */
export function useCampaignUserOptions(enabled: boolean) {
  const query = useListUsersApiV1UsersGet(
    { size: USERS_PAGE_SIZE, is_active: true },
    { query: { enabled } },
  )
  const options = useMemo(
    () => (query.data?.data.items ?? []).map(userOption),
    [query.data],
  )
  return { options, isLoading: query.isLoading }
}

/**
 * The backend-curated source list, for "Pick sources" (`provider_ids`) — the
 * same list every other Source picker reads (docs/project/source-lists.md).
 */
export function useCampaignSourceOptions(enabled: boolean) {
  const query = useListProviderOptionsApiV1ProvidersShortGet(undefined, {
    query: { enabled },
  })
  const options = useMemo<MultiSelectOption[]>(
    () =>
      (query.data?.data ?? []).map((provider) => ({
        value: provider.id,
        label: provider.name,
      })),
    [query.data],
  )
  return { options, isLoading: query.isLoading }
}
